import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  gcsReleaseObjectPrefix,
  type Artifact,
  type ArtifactChecksums,
  type ArtifactKind,
  type ArtifactProvider,
  type LinkMode,
  type LinkStatus,
  type LinkSummary,
  type ProviderWarning,
  type ReleaseSource,
  type ReleaseSummary,
} from '../../shared/api';
import { ApiError, NetworkError, type ApiClient } from '../api';
import CopyButton from '../components/CopyButton';
import SignInPanel from '../components/SignInPanel';
import {
  sessionApi,
  sessionStore,
  useIssuedLinks,
  useSessionSnapshot,
  useSessionToken,
  type SessionStore,
} from '../state';

type ReleasesState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'loaded'; releases: ReleaseSummary[]; warnings: ProviderWarning[] };

/** Issued-link inventory for the selected version (GCS objects only). */
type LinksState =
  | { status: 'idle' }
  | { status: 'loading'; version: string }
  | { status: 'error'; version: string; message: string }
  | { status: 'loaded'; version: string; links: LinkSummary[] };

type ArtifactsState =
  | { status: 'idle' }
  | { status: 'loading'; version: string }
  | { status: 'error'; version: string; message: string }
  | { status: 'loaded'; version: string; artifacts: Artifact[] };

const KIND_LABELS: Record<ArtifactKind, string> = {
  core_deb: 'Core DEB',
  trt_plugin_deb: 'TRT plugin DEB',
  frozen_zip: 'Frozen ZIP',
  stable_zip: 'Stable ZIP',
  scheduler_wheel: 'Scheduler wheel',
  decoder_tarball: 'Decoder tarball',
};

const SOURCE_LABELS: Record<ReleaseSource, string> = {
  gcs: 'GCS',
  github: 'GitHub',
  merged: 'GCS + GitHub',
};

const PROVIDER_LABELS: Record<ArtifactProvider, string> = {
  gcs: 'GCS',
  github: 'GitHub',
};

const LINK_MODE_LABELS: Record<LinkMode, string> = {
  managed: 'Managed',
  gcs_v4: 'GCS V4 signed',
};

const LINK_STATUS_LABELS: Record<LinkStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
};

/** How many issued links to pull per version (server caps GET /api/links at 100). */
const LINKS_PAGE_LIMIT = 100;

/** Parse a normalized semver ("1.4.2", "1.4.2-rc.1") for sorting. */
function parseSemver(version: string): { numbers: [number, number, number]; prerelease: string } {
  const [core = '', prerelease = ''] = version.split('-', 2);
  const parts = core.split('.').map((part) => Number.parseInt(part, 10));
  return {
    numbers: [parts[0] || 0, parts[1] || 0, parts[2] || 0],
    prerelease,
  };
}

/** Newest first; a stable release ranks above its own prereleases. */
function compareVersionsDesc(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  for (let i = 0; i < 3; i += 1) {
    if (pa.numbers[i] !== pb.numbers[i]) return pb.numbers[i] - pa.numbers[i];
  }
  if (pa.prerelease === pb.prerelease) return 0;
  if (pa.prerelease === '') return -1;
  if (pb.prerelease === '') return 1;
  return pb.prerelease.localeCompare(pa.prerelease);
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.code}, HTTP ${err.status})`;
  if (err instanceof NetworkError) return `Network error: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function formatBytes(sizeBytes: number | null): string {
  if (sizeBytes === null) return '—';
  if (!Number.isFinite(sizeBytes) || sizeBytes < 0) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB'] as const;
  let value = sizeBytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return unit === 0 ? `${value} ${units[unit]}` : `${value.toFixed(1)} ${units[unit]}`;
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/** One line per checksum the provider actually supplied (spec §5: never fabricated). */
function checksumLines(checksums: ArtifactChecksums): { label: string; value: string }[] {
  const lines: { label: string; value: string }[] = [];
  if (checksums.sha256Hex !== undefined) lines.push({ label: 'sha256', value: checksums.sha256Hex });
  if (checksums.md5Base64 !== undefined) lines.push({ label: 'md5', value: checksums.md5Base64 });
  if (checksums.crc32cBase64 !== undefined)
    lines.push({ label: 'crc32c', value: checksums.crc32cBase64 });
  return lines;
}

export interface ReleasesPageProps {
  /** Injectable for tests; defaults to the app-wide {@link sessionStore} singleton. */
  store?: SessionStore;
  /** Injectable for tests; defaults to the token-bound {@link sessionApi} client. */
  api?: ApiClient;
}

/**
 * Page 1 — release selector and artifact table (spec §5). Lists reconciled
 * releases from `GET /api/releases`, then renders the artifact inventory of
 * the selected version from `GET /api/releases/:version/artifacts`. The
 * selection is written to the session store so the command generator
 * (Page 2) can consume it.
 *
 * Download surfaces per provider — both are shown, not just GitHub's:
 *  - `github` artifacts carry a public `browser_download_url`, rendered
 *    directly in the artifact table;
 *  - `gcs` artifacts live in a private bucket, so their only download surface
 *    is an *issued link*. Those are listed per release from
 *    `GET /api/links?objectPathPrefix=releases/inference-engine/v/<version>/`,
 *    with the capability URL deliberately absent (it is returned exactly once
 *    at issuance); links issued in this browser session are matched up from
 *    the session store so their URL stays copyable.
 *
 * A GitHub provider failure (e.g. a rotated/expired `GITHUB_TOKEN` returning
 * 401 Bad credentials) arrives as a `warnings` entry rather than an error: the
 * GCS half of the list still renders, under a banner saying what is missing.
 */
export default function ReleasesPage({
  store = sessionStore,
  api = sessionApi,
}: ReleasesPageProps) {
  const token = useSessionToken(store);
  const snapshot = useSessionSnapshot(store);
  const [releasesState, setReleasesState] = useState<ReleasesState>({ status: 'loading' });
  const [artifactsState, setArtifactsState] = useState<ArtifactsState>({ status: 'idle' });
  const [linksState, setLinksState] = useState<LinksState>({ status: 'idle' });
  const [reloadNonce, setReloadNonce] = useState(0);

  const selectedVersion = snapshot.version;
  const sessionIssued = useIssuedLinks(store);

  // Fetch the release list whenever a token is available (or a reload is requested).
  useEffect(() => {
    if (token === null) return undefined;
    const controller = new AbortController();
    setReleasesState({ status: 'loading' });
    api.listReleases(controller.signal).then(
      (response) =>
        setReleasesState({
          status: 'loaded',
          releases: [...response.releases].sort((a, b) => compareVersionsDesc(a.version, b.version)),
          warnings: response.warnings ?? [],
        }),
      (err: unknown) => {
        if (!(err instanceof DOMException && err.name === 'AbortError'))
          setReleasesState({ status: 'error', message: describeError(err) });
      },
    );
    return () => controller.abort();
  }, [api, token, reloadNonce]);

  // Keep the session selection valid: auto-select the newest installable release.
  useEffect(() => {
    if (releasesState.status !== 'loaded' || releasesState.releases.length === 0) return;
    if (
      selectedVersion !== null &&
      releasesState.releases.some((release) => release.version === selectedVersion)
    ) {
      return;
    }
    const preferred =
      releasesState.releases.find((release) => release.available) ?? releasesState.releases[0];
    store.selectVersion(preferred.version);
  }, [releasesState, selectedVersion, store]);

  // Fetch the artifact table for the selected version.
  useEffect(() => {
    if (token === null || selectedVersion === null) {
      setArtifactsState({ status: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    const version = selectedVersion;
    setArtifactsState({ status: 'loading', version });
    api.listReleaseArtifacts(version, controller.signal).then(
      (response) =>
        setArtifactsState({ status: 'loaded', version, artifacts: response.artifacts }),
      (err: unknown) => {
        if (!(err instanceof DOMException && err.name === 'AbortError'))
          setArtifactsState({ status: 'error', version, message: describeError(err) });
      },
    );
    return () => controller.abort();
  }, [api, token, selectedVersion, reloadNonce]);

  // Fetch the links already issued for the selected version's GCS objects.
  useEffect(() => {
    if (token === null || selectedVersion === null) {
      setLinksState({ status: 'idle' });
      return undefined;
    }
    const controller = new AbortController();
    const version = selectedVersion;
    setLinksState({ status: 'loading', version });
    api
      .listLinks(
        { objectPathPrefix: gcsReleaseObjectPrefix(version), limit: LINKS_PAGE_LIMIT },
        controller.signal,
      )
      .then(
        (response) => setLinksState({ status: 'loaded', version, links: response.links }),
        (err: unknown) => {
          if (!(err instanceof DOMException && err.name === 'AbortError'))
            setLinksState({ status: 'error', version, message: describeError(err) });
        },
      );
    return () => controller.abort();
  }, [api, token, selectedVersion, reloadNonce]);

  if (token === null) {
    return (
      <section className="releases-page">
        <h1>Release Selector</h1>
        <SignInPanel
          reason="Listing releases and artifacts requires an operator token."
          store={store}
        />
      </section>
    );
  }

  return (
    <section className="releases-page">
      <div className="releases-page-header">
        <h1>Release Selector</h1>
        <button
          type="button"
          className="refresh-button"
          onClick={() => setReloadNonce((nonce) => nonce + 1)}
          disabled={releasesState.status === 'loading'}
        >
          {releasesState.status === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {releasesState.status === 'loaded' && releasesState.warnings.length > 0 && (
        <div className="provider-warning-panel" role="status">
          {releasesState.warnings.map((warning) => (
            <p key={`${warning.provider}:${warning.code}`}>
              <strong>{PROVIDER_LABELS[warning.provider]} unavailable</strong> — {warning.message}
              {warning.provider === 'github' && (
                <>
                  {' '}
                  This list and the artifact tables below cover{' '}
                  <strong>GCS artifacts only</strong> until it recovers. A 401 &ldquo;Bad
                  credentials&rdquo; means the deployment&rsquo;s <code>GITHUB_TOKEN</code> is
                  expired, revoked, or was never rolled out to the live deployment — see{' '}
                  <code>support-gui/README.md</code> and check{' '}
                  <Link to="/">provider health on the dashboard</Link>.
                </>
              )}
            </p>
          ))}
        </div>
      )}

      {releasesState.status === 'loading' && <p className="panel-note">Loading releases…</p>}

      {releasesState.status === 'error' && (
        <div className="error-panel" role="alert">
          <p>Failed to load releases: {releasesState.message}</p>
          <button type="button" onClick={() => setReloadNonce((nonce) => nonce + 1)}>
            Retry
          </button>
        </div>
      )}

      {releasesState.status === 'loaded' && releasesState.releases.length === 0 && (
        <p className="panel-note">No releases reconciled yet.</p>
      )}

      {releasesState.status === 'loaded' && releasesState.releases.length > 0 && (
        <div className="releases-layout">
          <nav className="release-list" aria-label="Releases">
            {releasesState.releases.map((release) => (
              <button
                key={release.version}
                type="button"
                className={`release-item${
                  release.version === selectedVersion ? ' release-item--selected' : ''
                }`}
                onClick={() => store.selectVersion(release.version)}
                aria-pressed={release.version === selectedVersion}
              >
                <span className="release-item-version">v{release.version}</span>
                <span className="release-item-meta">
                  <span className={`badge badge--source-${release.source}`}>
                    {SOURCE_LABELS[release.source]}
                  </span>
                  <span
                    className={`badge ${
                      release.available ? 'badge--available' : 'badge--unavailable'
                    }`}
                  >
                    {release.available ? 'Available' : 'No artifacts'}
                  </span>
                </span>
                <span className="release-item-date">{formatDateTime(release.releaseDate)}</span>
              </button>
            ))}
          </nav>

          <div className="artifact-panel">
            {artifactsState.status === 'idle' && (
              <p className="panel-note">Select a release to view its artifacts.</p>
            )}
            {artifactsState.status === 'loading' && (
              <p className="panel-note">Loading artifacts for v{artifactsState.version}…</p>
            )}
            {artifactsState.status === 'error' && (
              <div className="error-panel" role="alert">
                <p>
                  Failed to load artifacts for v{artifactsState.version}: {artifactsState.message}
                </p>
                <button type="button" onClick={() => setReloadNonce((nonce) => nonce + 1)}>
                  Retry
                </button>
              </div>
            )}
            {artifactsState.status === 'loaded' && (
              <ArtifactTable
                version={artifactsState.version}
                artifacts={artifactsState.artifacts}
                selectedArtifact={snapshot.artifact}
                linkCounts={countActiveLinksByArtifact(
                  linksState.status === 'loaded' && linksState.version === artifactsState.version
                    ? linksState.links
                    : [],
                )}
                onSelect={(artifact) =>
                  store.selectArtifact(
                    snapshot.artifact?.id === artifact.id ? null : artifact,
                  )
                }
              />
            )}

            <IssuedLinksPanel
              state={linksState}
              sessionUrls={sessionLinkUrls(sessionIssued, api)}
              onRetry={() => setReloadNonce((nonce) => nonce + 1)}
            />
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Count the currently-usable links per artifact id. Only `active` links count:
 * an expired or revoked row is history, and offering it as a download surface
 * would be a lie.
 *
 * Links issued without an `artifactId` hint (e.g. a manual object path typed
 * into the command generator) have no id to attribute to and are counted only
 * in the per-release panel, never here.
 */
function countActiveLinksByArtifact(links: readonly LinkSummary[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const link of links) {
    if (link.status !== 'active' || link.artifactId === null) continue;
    counts.set(link.artifactId, (counts.get(link.artifactId) ?? 0) + 1);
  }
  return counts;
}

/**
 * Absolutized capability URLs for links issued in THIS browser session, keyed
 * by link id.
 *
 * `GET /api/links` never returns a capability URL — a managed token and a
 * signed GCS URL are both returned exactly once, at issuance (spec §5). The
 * session store is the only place they survive, so this is what lets the panel
 * offer a copy button for a link the operator just created while still showing
 * every other link as metadata-only.
 */
function sessionLinkUrls(
  issued: readonly { link: LinkSummary; url: string }[],
  api: ApiClient,
): Map<string, string> {
  const urls = new Map<string, string>();
  for (const entry of issued) {
    urls.set(entry.link.id, api.resolveUrl(entry.url));
  }
  return urls;
}

/** Trailing path segment of a GCS object path — the artifact file name. */
function objectFileName(objectPath: string): string {
  const slash = objectPath.lastIndexOf('/');
  return slash === -1 ? objectPath : objectPath.slice(slash + 1);
}

interface IssuedLinksPanelProps {
  state: LinksState;
  sessionUrls: Map<string, string>;
  onRetry: () => void;
}

/**
 * Issued GCS download links for the selected release — the GCS counterpart to
 * the public `browser_download_url` shown for GitHub assets.
 */
function IssuedLinksPanel({ state, sessionUrls, onRetry }: IssuedLinksPanelProps) {
  if (state.status === 'idle') return null;
  return (
    <section className="issued-links-panel" aria-label="Issued download links">
      <div className="issued-links-header">
        <h2>Issued download links (GCS)</h2>
        {state.status === 'loaded' && (
          <span className="panel-note">
            {state.links.length === 0
              ? 'none yet'
              : `${state.links.length} for v${state.version}`}
          </span>
        )}
      </div>

      {state.status === 'loading' && (
        <p className="panel-note">Loading issued links for v{state.version}…</p>
      )}

      {state.status === 'error' && (
        <div className="error-panel" role="alert">
          <p>
            Failed to load issued links for v{state.version}: {state.message}
          </p>
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}

      {state.status === 'loaded' && state.links.length === 0 && (
        <p className="panel-note">
          No links issued for v{state.version} yet. GCS objects are private — issue one from the{' '}
          <Link to="/commands">install command generator</Link> to hand out a download.
        </p>
      )}

      {state.status === 'loaded' && state.links.length > 0 && (
        <table className="issued-links-table">
          <caption>
            Links issued against <code>{gcsReleaseObjectPrefix(state.version)}</code>. Capability
            URLs are shown only for links issued in this browser session — every link is returned
            exactly once, at issuance.
          </caption>
          <thead>
            <tr>
              <th scope="col">Object</th>
              <th scope="col">Mode</th>
              <th scope="col">Status</th>
              <th scope="col">Issued</th>
              <th scope="col">Expires</th>
              <th scope="col">Downloads</th>
              <th scope="col">URL</th>
            </tr>
          </thead>
          <tbody>
            {state.links.map((link) => {
              const url = sessionUrls.get(link.id);
              return (
                <tr key={link.id} className={`issued-link issued-link--${link.status}`}>
                  <td className="artifact-path">
                    <code title={link.objectPath}>{objectFileName(link.objectPath)}</code>
                  </td>
                  <td>{LINK_MODE_LABELS[link.mode]}</td>
                  <td>
                    <span className={`badge badge--link-${link.status}`}>
                      {LINK_STATUS_LABELS[link.status]}
                    </span>
                  </td>
                  <td>{formatDateTime(link.createdAt)}</td>
                  <td>{formatDateTime(link.expiresAt)}</td>
                  <td className="artifact-size">{link.downloadCount}</td>
                  <td>
                    {url === undefined ? (
                      <span className="panel-note" title="Returned once, at issuance">
                        not retrievable
                      </span>
                    ) : (
                      <CopyButton text={url} label="Copy URL" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface ArtifactTableProps {
  version: string;
  artifacts: Artifact[];
  selectedArtifact: Artifact | null;
  /** Active issued-link count per artifact id (GCS artifacts). */
  linkCounts: Map<string, number>;
  onSelect: (artifact: Artifact) => void;
}

function ArtifactTable({
  version,
  artifacts,
  selectedArtifact,
  linkCounts,
  onSelect,
}: ArtifactTableProps) {
  if (artifacts.length === 0) {
    return <p className="panel-note">No artifacts reconciled for v{version}.</p>;
  }
  return (
    <>
      <table className="artifact-table">
        <caption>
          Artifacts for v{version} — click a row to select it for the command generator
        </caption>
        <thead>
          <tr>
            <th scope="col">Kind</th>
            <th scope="col">Provider</th>
            <th scope="col">Python ABI</th>
            <th scope="col">Object path</th>
            <th scope="col">Size</th>
            <th scope="col">Updated</th>
            <th scope="col">Download</th>
            <th scope="col">Checksums</th>
          </tr>
        </thead>
        <tbody>
          {artifacts.map((artifact) => {
            const selected = selectedArtifact?.id === artifact.id;
            const checksums = checksumLines(artifact.checksums);
            return (
              <tr
                key={artifact.id}
                className={selected ? 'artifact-row artifact-row--selected' : 'artifact-row'}
                onClick={() => onSelect(artifact)}
                aria-selected={selected}
              >
                <td>{KIND_LABELS[artifact.kind]}</td>
                <td>
                  <span className={`badge badge--source-${artifact.provider}`}>
                    {PROVIDER_LABELS[artifact.provider]}
                  </span>
                </td>
                <td>{artifact.pythonAbi ?? 'any'}</td>
                <td className="artifact-path">
                  <code title={artifact.objectPath}>{artifact.objectPath}</code>
                </td>
                <td className="artifact-size">{formatBytes(artifact.sizeBytes)}</td>
                <td>{formatDateTime(artifact.updatedAt)}</td>
                <td className="artifact-download">
                  <ArtifactDownloadCell
                    artifact={artifact}
                    activeLinks={linkCounts.get(artifact.id) ?? 0}
                  />
                </td>
                <td className="artifact-checksums">
                  {checksums.length === 0
                    ? '—'
                    : checksums.map((line) => (
                        <div key={line.label}>
                          <span className="checksum-label">{line.label}</span>{' '}
                          <code title={line.value}>{line.value}</code>
                        </div>
                      ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {selectedArtifact !== null && (
        <p className="selection-banner">
          Selected for the command generator:{' '}
          <strong>
            {KIND_LABELS[selectedArtifact.kind]} v{selectedArtifact.version}
          </strong>{' '}
          <button type="button" onClick={() => onSelect(selectedArtifact)}>
            Clear selection
          </button>
        </p>
      )}
    </>
  );
}

interface ArtifactDownloadCellProps {
  artifact: Artifact;
  activeLinks: number;
}

/**
 * The artifact's download surface, which differs by provider by design:
 *
 *  - GitHub release assets are published behind a public
 *    `browser_download_url` (artifact_cache.public_url) and need no issuance,
 *    so the URL is offered directly.
 *  - GCS objects sit in a private bucket. There is no URL to show; the surface
 *    is an issued link, so the cell reports how many are currently active and
 *    routes to the generator to make one. (A GCS artifact is also the only kind
 *    a link CAN be issued for: GitHub artifact paths are `<tag>/<asset>`, which
 *    is outside the object-path allowlist POST /api/links enforces.)
 */
function ArtifactDownloadCell({ artifact, activeLinks }: ArtifactDownloadCellProps) {
  if (artifact.provider === 'github') {
    if (artifact.publicUrl === null) {
      return <span className="panel-note">—</span>;
    }
    return (
      <span className="artifact-download-github">
        <a href={artifact.publicUrl} target="_blank" rel="noreferrer noopener">
          Public URL
        </a>{' '}
        <CopyButton text={artifact.publicUrl} label="Copy" />
      </span>
    );
  }
  return (
    <span className="artifact-download-gcs">
      {activeLinks === 0 ? (
        <Link to="/commands">Issue link</Link>
      ) : (
        <>
          {activeLinks} active {activeLinks === 1 ? 'link' : 'links'}{' '}
          <Link to="/commands">issue another</Link>
        </>
      )}
    </span>
  );
}
